import { useEffect } from 'react'

import { useQuery } from '@apollo/client/react'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'
import { registerPostCountsFetcher } from '~publish/services/postCounts'

export const GetContentItemDraftsCount = graphql(`
  query GetContentItemDraftsCount(
    $organizationId: OrganizationId!
    $tags: TagComparator
  ) {
    contentItems(
      first: 1
      input: {
        organizationId: $organizationId
        filter: { contentStatus: draftContent, tags: $tags }
      }
    ) {
      totalCount
    }
  }
`)

// Module scope keeps the array identity stable, so the listeners bind once.
const CONTENT_ITEM_EVENTS = [
  PusherEvent.CONTENT_ITEM_CREATED,
  PusherEvent.CONTENT_ITEM_UPDATED,
  PusherEvent.CONTENT_ITEM_DELETED,
]

type UseContentItemDraftsCountOptions = {
  skip?: boolean
  tagIds?: string[]
}

type UseContentItemDraftsCountResult = {
  count: number
  loading: boolean
}

/**
 * How many channel-less drafts the organization has.
 *
 * A channel-less draft belongs to no channel, so `postCounts` — which groups by
 * channel — cannot see it. Surfaces that show a drafts total sum both sources.
 * Callers pass `skip` to leave these out where a channel-less draft cannot
 * match, such as a channel-filtered view, and `tagIds` to narrow them.
 */
export function useContentItemDraftsCount({
  skip = false,
  tagIds,
}: UseContentItemDraftsCountOptions = {}): UseContentItemDraftsCountResult {
  const organizationId = useOrganizationId()
  const isSkipped = skip || !organizationId

  const { data, loading, refetch } = useQuery(GetContentItemDraftsCount, {
    variables: {
      organizationId: organizationId ?? '',
      tags: buildTagComparator(tagIds),
    },
    skip: isSkipped,
    fetchPolicy: 'cache-and-network',
  })

  // A composer save refreshes the tab counts through this registry, not a
  // Pusher event, so the channel-less total joins it. Callers discard the
  // promise, so a failed refetch logs instead of rejecting.
  const refresh = useCallbackRef(async (): Promise<void> => {
    if (isSkipped) return
    try {
      await refetch()
    } catch (error) {
      debugLog('⚡ content item drafts count refetch failed', error)
    }
  })
  useEffect(() => registerPostCountsFetcher(refresh), [refresh])

  // A create or delete changes this total. So does an update that promotes
  // a draft to channel posts. A single number needs only a refetch.
  usePusherEvent(CONTENT_ITEM_EVENTS, (): void => {
    void refresh()
  })

  // Apollo keeps serving the last result once `skip` flips, so a count fetched
  // before a filter was applied has to be dropped here rather than reported on.
  return {
    count: isSkipped ? 0 : (data?.contentItems.totalCount ?? 0),
    loading: !isSkipped && loading && !data,
  }
}
